import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import Drawer from "../../components/drawer/drawer";
import Modal from "../../components/modal/modal";
import PageHeader from "../../components/page-header/page-header";
import { Product } from "../../types";
import { ProductForm } from "./components/product-form";
import { ProductTable } from "./components/product-table";
import { ProductFilters } from "./components/product-filters";
import { ReorderModal } from "./components/reorder-modal";
import { useProducts } from "./hooks/use-products";
import { useEffectiveBranchId } from "../../hooks/use-effective-branch-id";
import "./products.css";
import {
  getInitialProductManagementState,
  productManagementReducer,
  sortProductsByOrder,
} from "./utils/products-utils";
import { toast } from "sonner";

/**
 * Main Products page component
 * Manages product list, creation, editing, and deletion
 */
const Products: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();

  // Get effective branch ID (user's branch for regular users, selected branch for super admin)
  const effectiveBranchId = useEffectiveBranchId();

  // Use reducer for state management
  const [state, dispatch] = React.useReducer(
    productManagementReducer,
    getInitialProductManagementState()
  );

  // Reorder modal state
  const [isReorderModalOpen, setIsReorderModalOpen] = React.useState(false);
  const [reorderingProduct, setReorderingProduct] =
    React.useState<Product | null>(null);

  const {
    products,
    brands,
    models,
    isLoading,
    handleCreateProduct,
    handleUpdateProduct,
    handleDeleteProduct,
    handleDuplicateProduct,
    handleReorderProduct,
    // Filter state and handlers
    selectedBrandId,
    searchTerm,
    selectedStatus,
    handleBrandFilter,
    handleSearchFilter,
    handleStatusFilter,
    handleClearFilters,
    // Pagination from hook
    currentPage,
    itemsPerPage,
    totalPages,
    handlePageChange,
    handleItemsPerPageChange,
  } = useProducts(brandId, effectiveBranchId);

  // TODO: Improve error handling and add proper response types
  const handleSubmit = async (values: Partial<Product>): Promise<void> => {
    let response: any;
    if (state.editingProduct) {
      response = await handleUpdateProduct(state.editingProduct.id, values);
    } else {
      // If we're on a brand page, use the brandId
      const productData = brandId ? { ...values, brandId } : values;
      response = await handleCreateProduct(productData);
    }

    const hasSuccessField = typeof response?.success === "boolean";
    if (!hasSuccessField || response?.success) {
      toast.success(response?.message || "Операция выполнена успешно");
      dispatch({ type: "CLOSE_DRAWER" });
    } else {
      toast.error(response?.message || "Произошла ошибка");
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!state.deletingProduct) return;

    const success = await handleDeleteProduct(state.deletingProduct.id);
    if (success) {
      dispatch({ type: "CLOSE_DELETE_MODAL" });
    }
  };

  const openEditDrawer = (product: Product): void => {
    dispatch({ type: "OPEN_EDIT_DRAWER", payload: product });
  };

  const openCreateDrawer = (): void => {
    dispatch({ type: "OPEN_CREATE_DRAWER" });
  };

  const closeDrawer = (): void => {
    dispatch({ type: "CLOSE_DRAWER" });
  };

  const openDeleteModal = (product: Product): void => {
    dispatch({ type: "OPEN_DELETE_MODAL", payload: product });
  };

  const closeDeleteModal = (): void => {
    dispatch({ type: "CLOSE_DELETE_MODAL" });
  };

  const handleDuplicate = async (product: Product): Promise<void> => {
    const success = await handleDuplicateProduct(product);
    if (success) {
      // Product will be refreshed automatically by the hook
    }
  };

  const openReorderModal = (product: Product): void => {
    setReorderingProduct(product);
    setIsReorderModalOpen(true);
  };

  const closeReorderModal = (): void => {
    setIsReorderModalOpen(false);
    setReorderingProduct(null);
  };

  const handleReorder = async (newOrder: number): Promise<void> => {
    if (!reorderingProduct) return;

    const success = await handleReorderProduct(reorderingProduct.id, newOrder);
    if (success) {
      closeReorderModal();
    }
  };

  // Find current brand if viewing products for a specific brand
  const currentBrand = brandId ? brands.find((b) => b.id === brandId) : null;

  const getDrawerTitle = (editingProduct?: Product | null): string => {
    return editingProduct ? "Редактировать продукт" : "Создать продукт";
  };

  // Sort products by order field (server-side pagination already handles slicing)
  const sortedProducts = React.useMemo(() => {
    return sortProductsByOrder(products);
  }, [products]);

  return (
    <div className="products">
      <PageHeader
        title={currentBrand ? `Продукты ${currentBrand.name}` : "Продукты"}
        subtitle="Управление автомобилями для аренды"
        showBackButton={!!brandId}
        backButtonText="Назад к брендам"
        onBackClick={() => navigate("/brands")}
        actionButton={{
          text: "Добавить продукт",
          icon: "plus",
          onClick: openCreateDrawer,
        }}
      />

      <ProductFilters
        brands={brands}
        selectedBrandId={selectedBrandId}
        searchTerm={searchTerm}
        selectedStatus={selectedStatus}
        onBrandChange={handleBrandFilter}
        onSearchChange={handleSearchFilter}
        onStatusChange={handleStatusFilter}
        onClearFilters={handleClearFilters}
        isLoading={isLoading}
        disabledBrandFilter={!!brandId}
      />

      <ProductTable
        products={sortedProducts}
        brands={brands}
        models={models}
        isLoading={isLoading}
        onEdit={openEditDrawer}
        onDelete={openDeleteModal}
        onDuplicate={handleDuplicate}
        onReorder={openReorderModal}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      <Drawer
        title={getDrawerTitle(state.editingProduct)}
        isOpen={state.isDrawerOpen}
        onClose={closeDrawer}
        width={800}
      >
        <ProductForm
          initialValues={state.editingProduct}
          onSubmit={handleSubmit}
          onCancel={closeDrawer}
          isLoading={isLoading}
          brands={brands}
          models={models}
          {...(brandId && { selectedBrandId: brandId })}
        />
      </Drawer>

      <Modal
        isOpen={state.isDeleteModalOpen}
        onClose={closeDeleteModal}
        title="Подтвердите удаление"
        onConfirm={handleDelete}
      >
        <p>Вы уверены, что хотите удалить этот продукт?</p>
        <p>
          <strong>
            {state.deletingProduct?.brand} {state.deletingProduct?.model} (
            {state.deletingProduct?.year})
          </strong>
        </p>
        <p>Лот: {state.deletingProduct?.lotNumber}</p>
      </Modal>

      <ReorderModal
        isOpen={isReorderModalOpen}
        onClose={closeReorderModal}
        onConfirm={handleReorder}
        product={reorderingProduct}
        isLoading={isLoading}
      />
    </div>
  );
};

export default Products;
